import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowUpRight, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  apiError,
  useAcknowledgeAutoAddMutation,
  useAdminUsersQuery,
  useArchivePartnerMutation,
  useChangeEntitlementsMutation,
  useCreateUserMutation,
  useEntitlementsQuery,
  useLiftSuspensionMutation,
  usePartnerAuditQuery,
  usePartnerQuery,
  usePriceListQuery,
  useSetCreditScaleMutation,
  useRemovePriceMutation,
  useResetUserPasswordMutation,
  useSetPriceMutation,
  useSummaryQuery,
  useSuspendPartnerMutation,
  useUpdatePartnerMutation,
  useUpdateUserMutation,
  type PartnerPatch,
} from '@/store/api'
import type { DashboardUserRow, EntitlementChange, Partner, PriceKind, PriceListItem, SuspensionReason } from '@shared/types'
import { PageHeader } from '@/components/layout/AppShell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input, Label, FieldError } from '@/components/ui/input'
import { MetricCard } from '@/components/ui/metric-card'
import { Select } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, THead, Th, Tr, Td, SkeletonRows, Pagination } from '@/components/ui/data-table'
import { ErrorState } from '@/components/ui/empty-state'
import { Term } from '@/components/ui/term'
import { NotFound } from '@/pages/NotFound'
import { ApiKeysPanel, SigningKeysPanel } from '@/components/panels/KeyPanels'
import { TempPasswordDialog } from '@/components/panels/TempPasswordDialog'
import { fmtDate, fmtDateTime, fmtMoney, fmtNum, fmtPeriod } from '@/lib/format'

const SUSPENSION_LABELS: Record<SuspensionReason, string> = {
  non_payment: 'Non-payment (clause 6.7)',
  material_breach: 'Material breach (clause 17.2)',
}

export function PartnerDetail() {
  const { slug = '' } = useParams()
  const { data: partner, isLoading, isError, error, refetch } = usePartnerQuery(slug)

  // A 404 means the slug does not exist — a not-found, not a system fault.
  if (isError) {
    const status = (error as { status?: number } | undefined)?.status
    if (status === 404) return <NotFound />
    return <ErrorState onRetry={refetch} />
  }
  if (isLoading || !partner) return <div className="h-40 animate-pulse rounded-xl bg-muted" />

  return (
    <>
      <PageHeader
        title={partner.name}
        description={
          <span className="inline-flex flex-wrap items-center gap-2">
            {partner.groupName && <Badge variant="neutral">{partner.groupName}</Badge>}
            {partner.offers.agency && <Badge variant="post">Agency bundle</Badge>}
            {partner.offers.smb && <Badge variant="post">SMB</Badge>}
            <Badge variant="neutral">{partner.currency}</Badge>
            {partner.suspension && <Badge variant="revoked">Suspended</Badge>}
            {partner.archivedAt && <Badge variant="expired">Archived</Badge>}
            <Link
              to={`/p/${partner.slug}/overview`}
              className="inline-flex items-center gap-0.5 text-sm font-medium text-ata-light-blue hover:underline"
            >
              View as partner <ArrowUpRight className="size-3.5" />
            </Link>
          </span>
        }
      />
      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="commercial">Commercial</TabsTrigger>
          <TabsTrigger value="contract">Contract &amp; billing</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="keys">Keys</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="danger">Danger zone</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="pt-6">
          <OverviewTab partner={partner} />
        </TabsContent>
        <TabsContent value="commercial" className="pt-6">
          <CommercialTab partner={partner} />
        </TabsContent>
        <TabsContent value="contract" className="pt-6">
          <ContractTab partner={partner} />
        </TabsContent>
        <TabsContent value="users" className="pt-6">
          <UsersTab partner={partner} />
        </TabsContent>
        <TabsContent value="keys" className="space-y-8 pt-6">
          <ApiKeysPanel slug={partner.slug} />
          <SigningKeysPanel slug={partner.slug} />
        </TabsContent>
        <TabsContent value="activity" className="pt-6">
          <ActivityTab partner={partner} />
        </TabsContent>
        <TabsContent value="danger" className="space-y-4 pt-6">
          <SuspensionPanel partner={partner} />
          <ArchivePanel partner={partner} />
        </TabsContent>
      </Tabs>
    </>
  )
}

function Panel({ title, description, children }: { title: string; description?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-card p-5 shadow-sm">
      <h3 className="text-base font-semibold">{title}</h3>
      {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
      <div className="mt-4">{children}</div>
    </section>
  )
}

/** Save a partial patch and toast the outcome; returns whether it succeeded. */
function useSavePartner(slug: string) {
  const [update, { isLoading }] = useUpdatePartnerMutation()
  const save = async (patch: PartnerPatch, message = 'Saved') => {
    try {
      await update({ slug, patch }).unwrap()
      toast.success(message)
      return true
    } catch (err) {
      toast.error(apiError(err))
      return false
    }
  }
  return { save, isLoading }
}

// --- Overview -------------------------------------------------------------------

function OverviewTab({ partner }: { partner: Partner }) {
  const { data: s } = useSummaryQuery(partner.slug)
  const { save } = useSavePartner(partner.slug)
  const [notes, setNotes] = useState(partner.notes ?? '')
  useEffect(() => setNotes(partner.notes ?? ''), [partner.notes])
  const e = s?.entitlements

  return (
    <div className="space-y-6">
      {s && (
        <p className="text-sm text-muted-foreground">
          {fmtPeriod(s.period)} (current <Term id="billing-period" />) · previous period in captions
        </p>
      )}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <MetricCard
          label="Licence Entitlements"
          value={e ? fmtNum(e.held) : '…'}
          caption={
            e && s
              ? `${fmtMoney(e.rateCents, 'USD')} each · ${e.distanceToNextTier ? `${fmtNum(e.distanceToNextTier)} to $7.50` : 'lowest tier'} · last period ${fmtNum(s.previousHeld)}`
              : undefined
          }
        />
        <MetricCard label="Licences granted" value={s ? fmtNum(s.granted.current) : '…'} caption={s && `Last period ${fmtNum(s.granted.previous)}`} />
        <MetricCard
          label="Undrawn Entitlements"
          value={s ? fmtNum(s.undrawn.current) : '…'}
          caption={e && s && `Lapse ${fmtDate(e.lapsesAt)} · last period ${fmtNum(s.undrawn.previous)}`}
        />
        <MetricCard
          label="Pending activation"
          value={s ? fmtNum(s.pending) : '…'}
          caption={s && s.pendingExpired > 0 ? `${fmtNum(s.pendingExpired)} expired links` : undefined}
          captionClassName="text-ata-orange"
        />
        <MetricCard
          label="SMB owners (free / paid)"
          value={s ? `${fmtNum(s.smbFree.current)} / ${fmtNum(s.smbPaid.current)}` : '…'}
          caption={s && `${fmtNum(s.smbSites)} live sites · last period ${fmtNum(s.smbFree.previous)} / ${fmtNum(s.smbPaid.previous)}`}
        />
        <MetricCard
          label={s?.currentPeriodClosed ? 'Expansion Charges' : 'Expansion Charges (provisional)'}
          value={s ? fmtMoney(s.expansionCharges.current, s.currency) : '…'}
          caption={s && `Last period ${fmtMoney(s.expansionCharges.previous, s.currency)}`}
        />
      </div>
      {e && (
        <p className="text-sm text-muted-foreground">
          <Term id="reduction-floor" />: <span className="font-medium text-foreground">{fmtNum(e.reductionFloor)}</span>
          {e.scheduledDecrease && (
            <>
              {' '}
              · reducing to {fmtNum(e.scheduledDecrease.to)} from {fmtDate(e.scheduledDecrease.effectiveAt)}
            </>
          )}
        </p>
      )}

      <Panel title="Internal notes" description="Internal — never shown to partner or group users. Saves when you click away.">
        <textarea
          aria-label="Internal notes"
          value={notes}
          onChange={(ev) => setNotes(ev.target.value)}
          onBlur={() => notes !== (partner.notes ?? '') && save({ notes }, 'Notes saved')}
          rows={4}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-ring"
        />
      </Panel>
    </div>
  )
}

// --- Commercial -------------------------------------------------------------------

function CommercialTab({ partner }: { partner: Partner }) {
  return (
    <div className="max-w-4xl space-y-6">
      <EntitlementsPanel partner={partner} />
      <OffersPanel partner={partner} />
      <PriceListPanel partner={partner} />
      <CreditScalePanel partner={partner} />
    </div>
  )
}

const REASON_LABELS: Record<EntitlementChange['reason'], string> = {
  initial: 'Commencement',
  increase: 'Increase',
  decrease: 'Reduction',
  auto_add: 'Auto-added',
}

function EntitlementsPanel({ partner }: { partner: Partner }) {
  const { data, isLoading } = useEntitlementsQuery(partner.slug)
  const [change, { isLoading: changing }] = useChangeEntitlementsMutation()
  const [acknowledge] = useAcknowledgeAutoAddMutation()
  const [to, setTo] = useState('')
  const [error, setError] = useState<string | null>(null)
  const p = data?.position
  const target = parseInt(to, 10)
  const direction = p && Number.isFinite(target) ? (target > p.held ? 'up' : target < p.held ? 'down' : null) : null

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault()
    setError(null)
    try {
      await change({ slug: partner.slug, to: target }).unwrap()
      toast.success(direction === 'down' ? 'Reduction scheduled for next period' : 'Licence Entitlements updated')
      setTo('')
    } catch (err) {
      setError(apiError(err))
    }
  }

  return (
    <Panel
      title="Licence Entitlements"
      description={
        <>
          Minimum 500, steps of 100. The <Term id="licence-fee" /> rate is $10.00 below 1,000 and $7.50 from 1,000, fixed at the
          start of each <Term id="billing-period" />.
        </>
      }
    >
      {isLoading || !p ? (
        <div className="h-24 animate-pulse rounded-lg bg-muted" />
      ) : (
        <>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
            <Stat label="Held now" value={fmtNum(p.held)} />
            <Stat label="Rate this period" value={`${fmtMoney(p.rateCents, 'USD')} each`} />
            <Stat label="Rate next period" value={`${fmtMoney(p.nextPeriodRateCents, 'USD')} each`} />
            <Stat label="To the $7.50 tier" value={p.distanceToNextTier ? fmtNum(p.distanceToNextTier) : 'Reached'} />
            <Stat label="Granted" value={fmtNum(p.granted)} />
            <Stat label="Pending (live links)" value={fmtNum(p.pendingLive)} />
            <Stat label="Returning next period" value={fmtNum(p.returning)} />
            <Stat label={<Term id="reduction-floor" />} value={fmtNum(p.reductionFloor)} />
          </dl>
          {p.scheduledDecrease && (
            <p className="mt-3 text-sm text-ata-orange">
              Reducing to {fmtNum(p.scheduledDecrease.to)} from {fmtDate(p.scheduledDecrease.effectiveAt)}.
            </p>
          )}

          <form onSubmit={submit} className="mt-5 flex flex-wrap items-end gap-3 border-t border-border pt-4">
            <div>
              <Label htmlFor="ent-to">Change to</Label>
              <Input id="ent-to" type="number" min={500} step={100} className="w-36" value={to} onChange={(ev) => setTo(ev.target.value)} />
            </div>
            <Button type="submit" loading={changing} disabled={!direction}>
              {direction === 'down' ? 'Schedule reduction' : 'Apply increase'}
            </Button>
            <p className="basis-full text-xs text-muted-foreground">
              {direction === 'down'
                ? `Reductions take effect from the start of next period and cannot go below the reduction floor (${fmtNum(p.reductionFloor)}).`
                : 'Increases apply now. The added Entitlements are billed pro-rata for the rest of this period on the next statement.'}
            </p>
            <FieldError>{error}</FieldError>
          </form>
        </>
      )}

      {data && data.changes.length > 0 && (
        <div className="mt-6 overflow-x-auto">
          <h4 className="mb-2 text-sm font-semibold">History</h4>
          <Table>
            <THead>
              <Th>When</Th>
              <Th>Change</Th>
              <Th>Effective</Th>
              <Th>Rate</Th>
              <Th>By</Th>
              <Th right>Ops</Th>
            </THead>
            <tbody>
              {data.changes.map((c) => (
                <Tr key={c.id} className={c.cancelledAt ? 'opacity-50' : undefined}>
                  <Td className="whitespace-nowrap">{fmtDateTime(c.occurredAt)}</Td>
                  <Td>
                    <span className="font-medium">{REASON_LABELS[c.reason]}</span>{' '}
                    {c.reason === 'initial' ? fmtNum(c.to) : `${fmtNum(c.from)} → ${fmtNum(c.to)}`}
                    {c.cancelledAt && <span className="ml-2 text-xs text-muted-foreground">superseded</span>}
                  </Td>
                  <Td className="whitespace-nowrap">{fmtDate(c.effectiveAt)}</Td>
                  <Td>{fmtMoney(c.rateCents, 'USD')}</Td>
                  <Td className={c.actorEmail ? '' : 'text-muted-foreground'}>{c.actorEmail ?? 'system'}</Td>
                  <Td right>
                    {c.reason === 'auto_add' && !c.cancelledAt &&
                      (c.acknowledgedAt ? (
                        <span className="text-xs text-muted-foreground">Confirmed by {c.acknowledgedByEmail ?? 'admin'}</span>
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={async () => {
                            try {
                              await acknowledge({ slug: partner.slug, id: c.id }).unwrap()
                              toast.success('Auto-add confirmed')
                            } catch (err) {
                              toast.error(apiError(err))
                            }
                          }}
                        >
                          Confirm with partner
                        </Button>
                      ))}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Panel>
  )
}

function Stat({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium" data-numeric>
        {value}
      </dd>
    </div>
  )
}

function OffersPanel({ partner }: { partner: Partner }) {
  const { save, isLoading } = useSavePartner(partner.slug)
  const [currency, setCurrency] = useState(partner.currency)
  const [language, setLanguage] = useState(partner.language)
  useEffect(() => {
    setCurrency(partner.currency)
    setLanguage(partner.language)
  }, [partner.currency, partner.language])

  const toggle = (offer: 'agency' | 'smb', on: boolean) => {
    const next = { ...partner.offers, [offer]: on }
    if (!next.agency && !next.smb) return toast.error('At least one offer must stay enabled')
    void save({ offers: next }, `${offer === 'agency' ? 'Agency bundle' : 'SMB'} ${on ? 'enabled' : 'disabled'}`)
  }

  return (
    <Panel title="Offers, currency and language">
      <div className="space-y-3">
        <OfferRow label="Agency bundle" detail="Licence Entitlements for agencies." checked={partner.offers.agency} onChange={(v) => toggle('agency', v)} />
        <OfferRow label="SMB" detail="Enrol hosted websites into the brand's AI web agency." checked={partner.offers.smb} onChange={(v) => toggle('smb', v)} />
      </div>
      <form
        className="mt-5 flex flex-wrap items-end gap-3 border-t border-border pt-4"
        onSubmit={(ev) => {
          ev.preventDefault()
          void save({ currency: currency.trim().toUpperCase(), language: language.trim() })
        }}
      >
        <div>
          <Label htmlFor="cfg-cur">Currency</Label>
          <Input id="cfg-cur" maxLength={3} className="w-24" value={currency} onChange={(ev) => setCurrency(ev.target.value.toUpperCase())} />
        </div>
        <div>
          <Label htmlFor="cfg-lang">Language</Label>
          <Input id="cfg-lang" className="w-24" value={language} onChange={(ev) => setLanguage(ev.target.value)} />
        </div>
        <Button type="submit" variant="secondary" loading={isLoading} disabled={currency === partner.currency && language === partner.language}>
          Save
        </Button>
        <p className="basis-full text-xs text-muted-foreground">
          List prices are in this currency; statements convert to USD at close. Everything launches in English — the language is
          stored for the product to use later.
        </p>
      </form>
    </Panel>
  )
}

function OfferRow({ label, detail, checked, onChange }: { label: string; detail: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="text-sm font-medium">{label}</div>
        <p className="text-xs text-muted-foreground">{detail}</p>
      </div>
      <Switch checked={checked} aria-label={label} onCheckedChange={onChange} />
    </div>
  )
}

const KIND_LABELS: Record<PriceKind, string> = {
  pro: 'Pro',
  pro_upgrade: 'Pro upgrade',
  credit_pack: 'Credit pack',
  website_owner: 'Website Owner plan',
}

function PriceListPanel({ partner }: { partner: Partner }) {
  const { data, isLoading } = usePriceListQuery(partner.slug)
  const [remove] = useRemovePriceMutation()
  const [editing, setEditing] = useState<PriceListItem | 'new' | null>(null)
  const [removing, setRemoving] = useState<PriceListItem | null>(null)

  return (
    <Panel
      title="Price list"
      description={
        <>
          <Term id="list-price">List prices</Term> in {partner.currency}. Partners and WHMCS read them; every change bumps the
          version (now v{data?.version ?? partner.priceListVersion}). A change applies to new purchases only.
        </>
      }
    >
      <div className="overflow-x-auto">
        <Table>
          <THead>
            <Th>SKU</Th>
            <Th>Kind</Th>
            <Th>Name</Th>
            <Th>Band</Th>
            <Th right>Credits</Th>
            <Th right>List price</Th>
            <Th right>Actions</Th>
          </THead>
          {isLoading ? (
            <SkeletonRows cols={7} rows={3} />
          ) : (
            <tbody>
              {data?.items.length === 0 && (
                <Tr>
                  <Td colSpan={7} className="text-center text-muted-foreground">
                    No prices set yet.
                  </Td>
                </Tr>
              )}
              {data?.items.map((i) => (
                <Tr key={i.id}>
                  <Td className="font-mono text-xs">{i.sku}</Td>
                  <Td>{KIND_LABELS[i.kind]}</Td>
                  <Td>{i.name}</Td>
                  <Td className={i.band ? '' : 'text-muted-foreground'}>{i.band || '—'}</Td>
                  <Td right>{i.credits ? fmtNum(i.credits) : '—'}</Td>
                  <Td right>{fmtMoney(i.amountMinor, data.currency)}</Td>
                  <Td right>
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="secondary" onClick={() => setEditing(i)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" aria-label={`Remove ${i.sku}`} className="text-ata-red hover:bg-ata-red/10" onClick={() => setRemoving(i)}>
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          )}
        </Table>
      </div>
      <Button size="sm" className="mt-3" onClick={() => setEditing('new')}>
        Add price
      </Button>

      {editing && (
        <PriceDialog partner={partner} item={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={`Remove ${removing?.sku}?`}
        confirmLabel="Remove price"
        onConfirm={async () => {
          try {
            await remove({ slug: partner.slug, sku: removing!.sku }).unwrap()
            toast.success('Price removed')
            setRemoving(null)
          } catch (err) {
            toast.error(apiError(err))
          }
        }}
      >
        <p>
          WHMCS and in-app prices stop offering this product for {partner.name}. Purchases already made keep the price stored with
          them.
        </p>
      </ConfirmDialog>
    </Panel>
  )
}

function PriceDialog({ partner, item, onClose }: { partner: Partner; item: PriceListItem | null; onClose: () => void }) {
  const [setPrice, { isLoading }] = useSetPriceMutation()
  const [sku, setSku] = useState(item?.sku ?? '')
  const [kind, setKind] = useState<PriceKind>(item?.kind ?? 'pro')
  const [name, setName] = useState(item?.name ?? '')
  const [band, setBand] = useState(item?.band ?? '')
  const [credits, setCredits] = useState(item?.credits ? String(item.credits) : '')
  const [amount, setAmount] = useState(item ? (item.amountMinor / 100).toFixed(2) : '')
  const [error, setError] = useState<string | null>(null)
  const needsCredits = kind === 'credit_pack'

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault()
    setError(null)
    const amountMinor = Math.round(parseFloat(amount) * 100)
    if (!Number.isFinite(amountMinor) || amountMinor < 0) return setError('Enter a price of 0 or more')
    const creditCount = parseInt(credits, 10)
    if (needsCredits && !(creditCount > 0)) return setError('Credit packs need a credit amount')
    try {
      await setPrice({
        slug: partner.slug,
        sku: sku.trim(),
        item: { kind, name: name.trim(), band: band.trim(), credits: needsCredits ? creditCount : null, amountMinor },
      }).unwrap()
      toast.success(item ? 'Price updated' : 'Price added')
      onClose()
    } catch (err) {
      setError(apiError(err))
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={item ? `Edit ${item.sku}` : 'Add price'} description={`List price in ${partner.currency}.`}>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="pr-sku">SKU</Label>
              <Input id="pr-sku" required disabled={!!item} placeholder="e.g. credits-1000" value={sku} onChange={(ev) => setSku(ev.target.value.toLowerCase())} />
            </div>
            <div>
              <Label>Kind</Label>
              <Select
                ariaLabel="Kind"
                value={kind}
                onValueChange={(v) => setKind(v as PriceKind)}
                options={Object.entries(KIND_LABELS).map(([value, label]) => ({ value, label }))}
                className="w-full"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="pr-name">Name</Label>
            <Input id="pr-name" required value={name} onChange={(ev) => setName(ev.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="pr-band">Band</Label>
              <Input id="pr-band" placeholder="optional" value={band} onChange={(ev) => setBand(ev.target.value)} />
            </div>
            <div>
              <Label htmlFor="pr-credits">Credits</Label>
              <Input id="pr-credits" type="number" min={1} disabled={!needsCredits} value={needsCredits ? credits : ''} onChange={(ev) => setCredits(ev.target.value)} />
            </div>
            <div>
              <Label htmlFor="pr-amount">Price ({partner.currency})</Label>
              <Input id="pr-amount" type="number" min={0} step="0.01" required value={amount} onChange={(ev) => setAmount(ev.target.value)} />
            </div>
          </div>
          {kind === 'website_owner' && (
            <p className="text-xs text-muted-foreground">
              Sold to SMB owners as a monthly subscription that adds 2,000 AI credits a month. The in-app checkout sends sku
              website-owner.
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            The <Term id="expansion-charge" /> on each sale is 50% of this price, whatever the partner charges.
          </p>
          <FieldError>{error}</FieldError>
          <Button type="submit" className="w-full" loading={isLoading}>
            Save price
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}

type TierDraft = { fromCredits: string; perThousand: string }

/**
 * SMB monthly budgets are sold in 1,000-credit blocks on a sliding scale, like Pro credit
 * add-ons: the whole budget is priced at the rate of the highest tier it reaches.
 */
function CreditScalePanel({ partner }: { partner: Partner }) {
  const { data, isLoading } = usePriceListQuery(partner.slug)
  const [save, { isLoading: saving }] = useSetCreditScaleMutation()
  const [rows, setRows] = useState<TierDraft[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const saved = (data?.smbBudgetScale ?? []).map((t) => ({
    fromCredits: String(t.fromCredits),
    perThousand: (t.perThousandMinor / 100).toFixed(2),
  }))
  const tiers = rows ?? saved
  const update = (i: number, patch: Partial<TierDraft>) => setRows(tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)))

  const submit = async () => {
    setError(null)
    try {
      await save({
        slug: partner.slug,
        tiers: tiers.map((t, i) => ({
          fromCredits: i === 0 ? 1000 : parseInt(t.fromCredits, 10),
          perThousandMinor: Math.round(parseFloat(t.perThousand) * 100),
        })),
      }).unwrap()
      toast.success('Credit scale saved')
      setRows(null)
    } catch (err) {
      setError(apiError(err))
    }
  }

  return (
    <Panel
      title="SMB budget scale"
      description={
        <>
          The <Term id="list-price">list price</Term> of an SMB <Term id="monthly-credit-budget">monthly credit budget</Term>, in {partner.currency} per 1,000 credits. A budget is
          priced at the rate of the highest tier it reaches. Changes bump the price list version and apply to new purchases only.
        </>
      }
    >
      <Table>
        <THead>
          <Th>From (credits)</Th>
          <Th>Price per 1,000 ({partner.currency})</Th>
          <Th right>Actions</Th>
        </THead>
        {isLoading ? (
          <SkeletonRows cols={3} rows={3} />
        ) : (
          <tbody>
            {tiers.length === 0 && (
              <Tr>
                <Td colSpan={3} className="text-center text-muted-foreground">
                  No scale yet, so SMB budgets can't be sold.
                </Td>
              </Tr>
            )}
            {tiers.map((t, i) => (
              <Tr key={i}>
                <Td>
                  <Input
                    aria-label="From credits"
                    type="number"
                    min={1000}
                    step={1000}
                    disabled={i === 0}
                    value={i === 0 ? '1000' : t.fromCredits}
                    onChange={(ev) => update(i, { fromCredits: ev.target.value })}
                    className="w-32"
                  />
                </Td>
                <Td>
                  <Input
                    aria-label="Price per 1,000 credits"
                    type="number"
                    min={0}
                    step="0.01"
                    value={t.perThousand}
                    onChange={(ev) => update(i, { perThousand: ev.target.value })}
                    className="w-32"
                  />
                </Td>
                <Td right>
                  {i > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Remove tier"
                      className="text-ata-red hover:bg-ata-red/10"
                      onClick={() => setRows(tiers.filter((_, j) => j !== i))}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  )}
                </Td>
              </Tr>
            ))}
          </tbody>
        )}
      </Table>
      <FieldError>{error}</FieldError>
      <div className="mt-3 flex gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            const last = tiers.length ? parseInt(tiers[tiers.length - 1].fromCredits, 10) || 1000 : 0
            setRows([
              ...tiers,
              {
                fromCredits: String(last ? last + 5000 : 1000),
                perThousand: '',
              },
            ])
          }}
        >
          Add tier
        </Button>
        {rows && (
          <>
            <Button size="sm" loading={saving} onClick={submit}>
              Save scale
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRows(null)}>
              Discard
            </Button>
          </>
        )}
      </div>
    </Panel>
  )
}

// --- Contract & billing --------------------------------------------------------------

function ContractTab({ partner }: { partner: Partner }) {
  return (
    <div className="max-w-3xl space-y-6">
      <BillingPanel partner={partner} />
      <ContractPanel partner={partner} />
    </div>
  )
}

function BillingPanel({ partner }: { partner: Partner }) {
  const { save, isLoading } = useSavePartner(partner.slug)
  const [b, setB] = useState(partner.billing)
  useEffect(() => setB(partner.billing), [partner.billing])
  const field = (key: 'entity' | 'address' | 'country' | 'vatNumber') => ({
    value: b[key],
    onChange: (ev: React.ChangeEvent<HTMLInputElement>) => setB({ ...b, [key]: ev.target.value }),
  })

  return (
    <Panel title="Billing details" description="Printed on every statement. Visible to the partner.">
      <form
        className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        onSubmit={(ev) => {
          ev.preventDefault()
          void save({ billing: b }, 'Billing details saved')
        }}
      >
        <div>
          <Label htmlFor="b-entity">Billing entity</Label>
          <Input id="b-entity" {...field('entity')} />
        </div>
        <div>
          <Label htmlFor="b-country">Country</Label>
          <Input id="b-country" {...field('country')} />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="b-address">Address</Label>
          <Input id="b-address" {...field('address')} />
        </div>
        <div>
          <Label htmlFor="b-vat">VAT number</Label>
          <Input id="b-vat" {...field('vatNumber')} />
        </div>
        <label className="flex items-center gap-2 self-end pb-2 text-sm">
          <Switch checked={b.reverseCharge} onCheckedChange={(v) => setB({ ...b, reverseCharge: v })} aria-label="Reverse charge" />
          Reverse charge applies
        </label>
        <div className="sm:col-span-2">
          <Button type="submit" loading={isLoading}>
            Save billing details
          </Button>
        </div>
      </form>
    </Panel>
  )
}

function ContractPanel({ partner }: { partner: Partner }) {
  const { save, isLoading } = useSavePartner(partner.slug)
  const c = partner.contract
  const [form, setForm] = useState(() => contractForm(partner))
  useEffect(() => setForm(contractForm(partner)), [partner])
  const set = (key: keyof typeof form) => (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [key]: ev.target.value })
  const months = (v: string) => (v.trim() ? parseInt(v, 10) : null)

  return (
    <Panel title="Contract details" description="Internal — never shown to partner or group users.">
      <form
        className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        onSubmit={(ev) => {
          ev.preventDefault()
          void save(
            {
              contract: {
                commencementDate: form.commencementDate || null,
                initialTermMonths: months(form.initialTermMonths),
                renewalTermMonths: months(form.renewalTermMonths),
                licenceVolumeAtCommencement: months(form.licenceVolume),
                territory: form.territory,
                dpaDate: form.dpaDate || null,
                escalationContacts: form.escalationContacts,
                scheduleVariations: form.scheduleVariations,
              },
            },
            'Contract details saved',
          )
        }}
      >
        <div>
          <Label htmlFor="c-start">Commencement date</Label>
          <Input id="c-start" type="date" value={form.commencementDate} onChange={set('commencementDate')} />
        </div>
        <div>
          <Label htmlFor="c-volume">Licence Volume at commencement</Label>
          <Input id="c-volume" type="number" min={0} value={form.licenceVolume} onChange={set('licenceVolume')} />
        </div>
        <div>
          <Label htmlFor="c-initial">Initial term (months)</Label>
          <Input id="c-initial" type="number" min={1} value={form.initialTermMonths} onChange={set('initialTermMonths')} />
        </div>
        <div>
          <Label htmlFor="c-renewal">Renewal term (months)</Label>
          <Input id="c-renewal" type="number" min={1} value={form.renewalTermMonths} onChange={set('renewalTermMonths')} />
        </div>
        <div>
          <Label htmlFor="c-territory">Territory</Label>
          <Input id="c-territory" value={form.territory} onChange={set('territory')} />
        </div>
        <div>
          <Label htmlFor="c-dpa">DPA date</Label>
          <Input id="c-dpa" type="date" value={form.dpaDate} onChange={set('dpaDate')} />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="c-escalation">Escalation contacts</Label>
          <Input id="c-escalation" value={form.escalationContacts} onChange={set('escalationContacts')} />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="c-variations">Variations from the template Schedule 1</Label>
          <textarea
            id="c-variations"
            rows={3}
            value={form.scheduleVariations}
            onChange={set('scheduleVariations')}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <dl className="grid grid-cols-3 gap-4 rounded-lg bg-muted/50 p-3 text-sm sm:col-span-2">
          <Stat label="Current term ends" value={fmtDate(c?.termEndDate)} />
          <Stat label="Non-renewal notice by" value={fmtDate(c?.nonRenewalNoticeDate)} />
          <Stat label="Price-change notice by" value={fmtDate(c?.priceChangeNoticeDate)} />
        </dl>
        <div className="sm:col-span-2">
          <Button type="submit" loading={isLoading}>
            Save contract details
          </Button>
        </div>
      </form>
    </Panel>
  )
}

function contractForm(partner: Partner) {
  const c = partner.contract
  const num = (v: number | null | undefined) => (v == null ? '' : String(v))
  return {
    commencementDate: c?.commencementDate ?? '',
    initialTermMonths: num(c?.initialTermMonths),
    renewalTermMonths: num(c?.renewalTermMonths),
    licenceVolume: num(c?.licenceVolumeAtCommencement),
    territory: c?.territory ?? '',
    dpaDate: c?.dpaDate ?? '',
    escalationContacts: c?.escalationContacts ?? '',
    scheduleVariations: c?.scheduleVariations ?? '',
  }
}

// --- Users ---------------------------------------------------------------------------

const ROLE_LABELS = { atarim_admin: 'Atarim admin', partner_user: 'Partner user', group_user: 'Group user' } as const

/**
 * Who can see this brand: its assigned partner users plus the group users of its group.
 * Atarim admins are managed on their own page and are never listed (or reset) from here.
 */
function UsersTab({ partner }: { partner: Partner }) {
  const { data: users, isLoading } = useAdminUsersQuery()
  const [updateUser, { isLoading: unassigning }] = useUpdateUserMutation()
  const [resetPassword, { isLoading: resetting }] = useResetUserPasswordMutation()
  const [createOpen, setCreateOpen] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)
  const [reveal, setReveal] = useState<{ email: string; password: string } | null>(null)
  const [confirmRemove, setConfirmRemove] = useState<DashboardUserRow | null>(null)
  const [confirmReset, setConfirmReset] = useState<DashboardUserRow | null>(null)

  const visible = users?.filter(
    (u) =>
      (u.role === 'partner_user' && u.partners.some((p) => p.id === partner.id)) ||
      (u.role === 'group_user' && !!partner.groupId && u.group?.id === partner.groupId),
  )

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">Dashboard users</h3>
          <p className="text-xs text-muted-foreground">
            Partner users see the brands they're assigned to; group users see every brand in their group.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setAssignOpen(true)}>
            Assign existing user
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            Create user
          </Button>
        </div>
      </div>
      <Table>
        <THead>
          <Th>Email</Th>
          <Th>Role</Th>
          <Th>Access</Th>
          <Th right>Actions</Th>
        </THead>
        {isLoading ? (
          <SkeletonRows cols={4} rows={3} />
        ) : (
          <tbody>
            {visible?.length === 0 && (
              <Tr>
                <Td colSpan={4} className="text-center text-muted-foreground">
                  No users can see {partner.name} yet.
                </Td>
              </Tr>
            )}
            {visible?.map((u) => (
              <Tr key={u.id}>
                <Td className="font-medium">{u.email}</Td>
                <Td>
                  <Badge variant="neutral">{ROLE_LABELS[u.role]}</Badge>
                </Td>
                <Td className="text-muted-foreground">
                  {u.role === 'group_user' ? `All of ${u.group?.name ?? 'the group'}` : u.partners.map((p) => p.name).join(', ')}
                </Td>
                <Td right>
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" size="sm" onClick={() => setConfirmReset(u)}>
                      Reset password
                    </Button>
                    {u.role === 'partner_user' && (
                      <Button variant="ghost" size="sm" className="text-ata-red hover:bg-ata-red/10" onClick={() => setConfirmRemove(u)}>
                        Remove from brand
                      </Button>
                    )}
                  </div>
                </Td>
              </Tr>
            ))}
          </tbody>
        )}
      </Table>

      <ConfirmDialog
        open={!!confirmRemove}
        onOpenChange={(o) => !o && setConfirmRemove(null)}
        title={`Remove ${confirmRemove?.email} from ${partner.name}?`}
        confirmLabel="Remove from brand"
        loading={unassigning}
        onConfirm={async () => {
          const u = confirmRemove!
          try {
            await updateUser({ id: u.id, partnerIds: u.partners.filter((p) => p.id !== partner.id).map((p) => p.id) }).unwrap()
            toast.success(`${u.email} removed from ${partner.name}`)
            setConfirmRemove(null)
          } catch (err) {
            toast.error(apiError(err))
          }
        }}
      >
        <p>
          They lose access to {partner.name} straight away. Their account is kept
          {confirmRemove && confirmRemove.partners.length > 1 ? ', along with access to their other brands' : ''}.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!confirmReset}
        onOpenChange={(o) => !o && setConfirmReset(null)}
        title={`Reset the password for ${confirmReset?.email}?`}
        confirmLabel="Reset password"
        loading={resetting}
        onConfirm={async () => {
          const u = confirmReset!
          try {
            const res = await resetPassword(u.id).unwrap()
            setConfirmReset(null)
            setReveal({ email: u.email, password: res.tempPassword })
          } catch (err) {
            toast.error(apiError(err))
          }
        }}
      >
        <p>
          They are signed out everywhere immediately and must set a new password using the temporary one you'll be shown next.
        </p>
      </ConfirmDialog>

      <CreateUserDialog partner={partner} open={createOpen} onOpenChange={setCreateOpen} onCreated={(email, password) => setReveal({ email, password })} />
      <AssignUserDialog partner={partner} open={assignOpen} onOpenChange={setAssignOpen} />
      <TempPasswordDialog reveal={reveal} onClose={() => setReveal(null)} />
    </div>
  )
}

const NO_USER = '__none'

/** Attach an existing partner user to this brand as well — creating them again would 409 on the email. */
function AssignUserDialog({ partner, open, onOpenChange }: { partner: Partner; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { data: users } = useAdminUsersQuery()
  const [updateUser, { isLoading }] = useUpdateUserMutation()
  const [userId, setUserId] = useState(NO_USER)
  const [error, setError] = useState<string | null>(null)

  const candidates = (users ?? []).filter((u) => u.role === 'partner_user' && !u.partners.some((p) => p.id === partner.id))

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const user = candidates.find((u) => u.id === userId)
    if (!user) return setError('Choose a user to assign.')
    try {
      await updateUser({ id: user.id, partnerIds: [...user.partners.map((p) => p.id), partner.id] }).unwrap()
      toast.success(`${user.email} assigned to ${partner.name}`)
      setUserId(NO_USER)
      onOpenChange(false)
    } catch (err) {
      setError(apiError(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Assign existing user"
        description="Adds this brand to a partner user's existing assignments. They keep everything they already have access to."
      >
        {candidates.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Every partner user is already assigned to {partner.name}. Use <strong>Create user</strong> to add a new one.
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Partner user</Label>
              <Select
                ariaLabel="Partner user"
                value={userId}
                onValueChange={setUserId}
                className="w-full"
                options={[
                  { value: NO_USER, label: 'Choose a user…' },
                  ...candidates.map((u) => ({
                    value: u.id,
                    label: u.partners.length ? `${u.email} — also on ${u.partners.map((p) => p.name).join(', ')}` : u.email,
                  })),
                ]}
              />
            </div>
            <FieldError>{error}</FieldError>
            <Button type="submit" className="w-full" loading={isLoading}>
              Assign to {partner.name}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** Partner users are assigned to this brand; group users see the whole group. Admins are created on the Atarim admins page. */
function CreateUserDialog({
  partner,
  open,
  onOpenChange,
  onCreated,
}: {
  partner: Partner
  open: boolean
  onOpenChange: (o: boolean) => void
  onCreated: (email: string, tempPassword: string) => void
}) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'partner_user' | 'group_user'>('partner_user')
  const [error, setError] = useState<string | null>(null)
  const [create, { isLoading }] = useCreateUserMutation()

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      const res = await create(
        role === 'group_user' ? { email, role, groupId: partner.groupId! } : { email, role, partnerIds: [partner.id] },
      ).unwrap()
      onOpenChange(false)
      setEmail('')
      onCreated(res.user.email, res.tempPassword)
    } catch (err) {
      setError(apiError(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Create user">
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="u-email">Email</Label>
            <Input id="u-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <Label>Access</Label>
            <Select
              ariaLabel="Access"
              value={role}
              onValueChange={(v) => setRole(v as 'partner_user' | 'group_user')}
              options={[
                { value: 'partner_user', label: `Partner user — ${partner.name} only` },
                ...(partner.groupId ? [{ value: 'group_user', label: `Group user — all of ${partner.groupName ?? 'the group'}` }] : []),
              ]}
              className="w-full"
            />
          </div>
          <FieldError>{error}</FieldError>
          <Button type="submit" className="w-full" loading={isLoading}>
            Create user
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// --- Activity ---------------------------------------------------------------------

const ACTION_LABELS: Record<string, string> = {
  'partner.create': 'Partner created',
  'partner.update': 'Details changed',
  'partner.archive': 'Partner archived',
  'partner.restore': 'Partner restored',
  'partner.suspend': 'Partner suspended',
  'partner.unsuspend': 'Suspension lifted',
  'partner.checkout_url': 'Checkout URL changed',
  'entitlements.change': 'Licence Entitlements changed',
  'entitlements.auto_add': 'Licence Entitlements auto-added',
  'entitlements.acknowledge': 'Auto-add confirmed',
  'price_list.set': 'Price set',
  'price_list.remove': 'Price removed',
  'group.create': 'Group created',
  'group.rename': 'Group renamed',
  'platform.settings_sync_failed': 'Platform settings sync failed',
  'license.provision': 'Licence created',
  'license.activate': 'Licence activated',
  'license.revoke': 'Licence revoked',
  'license.rename': 'Licence renamed',
  'license.regenerate_link': 'Activation link regenerated',
  'signing_key.generate': 'Signing key generated',
  'signing_key.rotate': 'Signing key rotated',
  'signing_key.revoke': 'Signing key revoked',
  'api_key.generate': 'API key generated',
  'api_key.rotate': 'API key rotated',
  'api_key.revoke': 'API key revoked',
  'whitelabel.update': 'Whitelabel updated',
  'whitelabel.asset_upload': 'Whitelabel asset uploaded',
}

/** Reads the activity log that every mutation writes to. Admin-only, read-only. */
function ActivityTab({ partner }: { partner: Partner }) {
  const [page, setPage] = useState(1)
  const { data, isLoading } = usePartnerAuditQuery({ slug: partner.slug, page })

  return (
    <>
      <div className="mb-3">
        <h3 className="text-base font-semibold">Activity</h3>
        <p className="text-xs text-muted-foreground">
          Every change to this brand, its Entitlements, prices, licences and keys. Internal — never shown to partner users.
        </p>
      </div>
      <Table>
        <THead>
          <Th>When</Th>
          <Th>Action</Th>
          <Th>Actor</Th>
          <Th>Detail</Th>
        </THead>
        {isLoading ? (
          <SkeletonRows cols={4} />
        ) : (
          <tbody>
            {data!.rows.length === 0 && (
              <Tr>
                <Td colSpan={4} className="text-center text-muted-foreground">
                  Nothing recorded yet.
                </Td>
              </Tr>
            )}
            {data!.rows.map((e) => (
              <Tr key={e.id}>
                <Td className="whitespace-nowrap">{fmtDateTime(e.occurredAt)}</Td>
                <Td className="font-medium">{ACTION_LABELS[e.action] ?? e.action}</Td>
                <Td className={e.actorEmail ? '' : 'text-muted-foreground'}>{e.actorEmail ?? 'system'}</Td>
                <Td className="max-w-[24rem] truncate font-mono text-xs text-muted-foreground" title={e.detail}>
                  {e.detail === '{}' ? '—' : e.detail}
                </Td>
              </Tr>
            ))}
          </tbody>
        )}
      </Table>
      {data && <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
    </>
  )
}

// --- Danger zone --------------------------------------------------------------------

function SuspensionPanel({ partner }: { partner: Partner }) {
  const [suspend, { isLoading: suspending }] = useSuspendPartnerMutation()
  const [lift, { isLoading: lifting }] = useLiftSuspensionMutation()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState<SuspensionReason>('non_payment')
  const [note, setNote] = useState('')
  const s = partner.suspension

  return (
    <div className="max-w-2xl rounded-xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold">{s ? 'Suspended' : 'Suspend partner'}</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {s ? (
              <>
                Since {fmtDateTime(s.suspendedAt)} — {SUSPENSION_LABELS[s.reason]}
                {s.note && <>. {s.note}</>}. New agency licences and SMB enrolments are blocked; existing customers keep running
                and keep being billed.
              </>
            ) : (
              <>
                For non-payment (6.7) or material breach (17.2). Blocks new agency licences and SMB enrolments. Existing
                customers keep running and keep being billed. The partner sees that it is suspended.
              </>
            )}
          </p>
        </div>
        {s ? (
          <Button
            variant="secondary"
            loading={lifting}
            onClick={async () => {
              try {
                await lift(partner.slug).unwrap()
                toast.success('Suspension lifted')
              } catch (err) {
                toast.error(apiError(err))
              }
            }}
          >
            Lift suspension
          </Button>
        ) : (
          <Button variant="secondary" className="text-ata-red" onClick={() => setOpen(true)}>
            Suspend
          </Button>
        )}
      </div>

      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={`Suspend ${partner.name}?`}
        confirmLabel="Suspend partner"
        loading={suspending}
        onConfirm={async () => {
          try {
            await suspend({ slug: partner.slug, reason, note }).unwrap()
            toast.success('Partner suspended')
            setOpen(false)
            setNote('')
          } catch (err) {
            toast.error(apiError(err))
          }
        }}
      >
        <div className="space-y-3">
          <p>New agency licences and SMB enrolments stop immediately. Lifting the suspension restores everything at once.</p>
          <div>
            <Label>Reason</Label>
            <Select
              ariaLabel="Suspension reason"
              value={reason}
              onValueChange={(v) => setReason(v as SuspensionReason)}
              options={Object.entries(SUSPENSION_LABELS).map(([value, label]) => ({ value, label }))}
              className="w-full"
            />
          </div>
          <div>
            <Label htmlFor="susp-note">Note (internal)</Label>
            <Input id="susp-note" placeholder="e.g. Invoice 1042 overdue" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
      </ConfirmDialog>
    </div>
  )
}

/**
 * Archiving is the only removal there is. Every child FK cascades, so deleting a partner
 * would take its licences, transactions and statements with it.
 */
function ArchivePanel({ partner }: { partner: Partner }) {
  const [open, setOpen] = useState(false)
  const [archive, { isLoading }] = useArchivePartnerMutation()
  const archived = !!partner.archivedAt

  const run = async (undo: boolean) => {
    try {
      await archive({ slug: partner.slug, undo }).unwrap()
      toast.success(undo ? 'Partner restored' : 'Partner archived')
      setOpen(false)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <div className="max-w-2xl rounded-xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold">{archived ? 'Archived partner' : 'Archive partner'}</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {archived
              ? 'Hidden from the partner list and cannot add licences. All history is intact.'
              : 'Hides the partner and stops new licences. Licences, transactions, statements and keys are all kept — partners are never deleted.'}
          </p>
        </div>
        {archived ? (
          <Button variant="secondary" loading={isLoading} onClick={() => run(true)}>
            Restore
          </Button>
        ) : (
          <Button variant="secondary" className="text-ata-red" onClick={() => setOpen(true)}>
            Archive
          </Button>
        )}
      </div>
      <ConfirmDialog open={open} onOpenChange={setOpen} title={`Archive ${partner.name}?`} confirmLabel="Archive partner" loading={isLoading} onConfirm={() => run(false)}>
        <p>
          The partner disappears from the partner list and can no longer add licences. Existing licences keep working and nothing
          is deleted — you can restore it at any time.
        </p>
      </ConfirmDialog>
    </div>
  )
}
